import { useCallback, useEffect, useRef, useState } from 'react'

import { AnimatedMove, BoardInfo } from '../GameApi/BaseGameApi'
import { applyAnimatedMove } from '../services/boardAnimation'

export interface AnimatedBoardState {
  displayedBoard: BoardInfo
  flying: AnimatedMove | null
  onArrived: () => void
}

interface Pending {
  move: AnimatedMove
  // true when animating an undo: slide the checker back, then snap to the
  // authoritative incoming board (which also restores any hit victim a plain
  // reverse slide would not re-create).
  reverse: boolean
}

/**
 * Buffers a displayed board that lags the incoming board so the opponent's
 * moves can play one-at-a-time. New moves animate forward; a move list that
 * shrinks *within the same turn* (an undo) animates the removed move in reverse.
 * `instant` (reduced motion / no WAAPI) snaps.
 *
 * Turn boundaries are handled HERE, via `turnKey` (the authoritative
 * `gameState.turn`): when it changes we baseline fresh and snap, never reverse.
 * This is the one case where a shrinking `liveMoves` (a turn's moves clearing to
 * `[]`) must NOT be read as an undo. Because `turnKey` and `liveMoves` come from
 * the same model snapshot, the boundary is evaluated atomically with the move
 * list — so correctness no longer depends on the parent remounting in time.
 */
export function useAnimatedBoard(
  board: BoardInfo,
  liveMoves: AnimatedMove[],
  instant: boolean,
  turnKey: number,
): AnimatedBoardState {
  const [displayed, setDisplayed] = useState<BoardInfo>(board)
  const [pending, setPending] = useState<Pending | null>(null)
  // Baseline at mount: moves already present are shown as-is, not replayed.
  const cursorRef = useRef<number>(liveMoves.length)
  // The last caught-up move list, so an undo can recover the move it removed.
  const knownMovesRef = useRef<AnimatedMove[]>(liveMoves)
  // The turn whose moves the cursor is tracking; a change is a hard reset.
  const turnKeyRef = useRef<number>(turnKey)
  // Latest board, read by onArrived without making it a dependency (which would
  // restart the in-flight animation on every incoming snapshot).
  const boardRef = useRef<BoardInfo>(board)
  boardRef.current = board

  useEffect(() => {
    // Turn boundary: rebaseline and snap. Never reverse across a turn — a
    // cleared `liveTurn` is not an undo. (Also covers the `instant` path, which
    // wants the same snap-and-baseline behaviour.)
    if (instant || turnKey !== turnKeyRef.current) {
      turnKeyRef.current = turnKey
      cursorRef.current = liveMoves.length
      knownMovesRef.current = liveMoves
      setPending(null)
      setDisplayed(board)
      return
    }
    if (pending) {
      return
    }
    const cursor = cursorRef.current
    if (liveMoves.length > cursor) {
      // Forward: animate the next appended move.
      setPending({ move: liveMoves[cursor], reverse: false })
      return
    }
    if (liveMoves.length < cursor) {
      // Same-turn undo: animate the reverse of the move that was just removed.
      const undone = knownMovesRef.current[cursor - 1]
      if (undone) {
        setPending({ move: reverseMove(undone), reverse: true })
      } else {
        cursorRef.current = liveMoves.length
        setDisplayed(board)
      }
      return
    }
    // Caught up: remember the current move list for a future undo.
    knownMovesRef.current = liveMoves
  }, [board, liveMoves, pending, instant, turnKey])

  const onArrived = useCallback(() => {
    if (!pending) {
      return
    }
    if (pending.reverse) {
      cursorRef.current -= 1
      // Snap to the authoritative board (already reflects the undo).
      setDisplayed(boardRef.current)
    } else {
      setDisplayed((d) => applyAnimatedMove(d, pending.move))
      cursorRef.current += 1
    }
    setPending(null)
  }, [pending])

  const flying = pending?.move ?? null
  const caughtUp = pending === null && cursorRef.current === liveMoves.length
  const displayedBoard = caughtUp ? board : displayed

  return { displayedBoard, flying, onArrived }
}

function reverseMove(move: AnimatedMove): AnimatedMove {
  return {
    from: move.to,
    to: move.from,
    owner: move.owner,
    isHit: false,
    isBearOff: false,
  }
}
